import type { FieldErrors, UseFormRegister } from "react-hook-form";
import { Input, Textarea } from "@nutrayan/ui";
import type { ProductFieldsValues } from "@/lib/catalog-api";

// The editable product body, shared by Create and Edit so validation + UI live in
// one place. Generic over any form whose values extend ProductFieldsValues.
interface Props<T extends ProductFieldsValues> {
  register: UseFormRegister<T>;
  errors: FieldErrors<ProductFieldsValues>;
  disabled?: boolean;
}

export function ProductFields<T extends ProductFieldsValues>({
  register,
  errors,
  disabled = false,
}: Props<T>) {
  // Cast to the base register so the shared field names typecheck across forms.
  const reg = register as unknown as UseFormRegister<ProductFieldsValues>;
  return (
    <>
      <div className="sm:col-span-2">
        <label className="text-body-sm text-ink" htmlFor="pf-name">
          Name
        </label>
        <Input id="pf-name" disabled={disabled} {...reg("name")} invalid={!!errors.name} />
        {errors.name ? (
          <p className="text-caption text-terracotta mt-1">{errors.name.message}</p>
        ) : null}
      </div>

      <div className="sm:col-span-2">
        <label className="text-body-sm text-ink" htmlFor="pf-short">
          Short description
        </label>
        <Textarea
          id="pf-short"
          disabled={disabled}
          rows={2}
          {...reg("shortDescription")}
          invalid={!!errors.shortDescription}
        />
        {errors.shortDescription ? (
          <p className="text-caption text-terracotta mt-1">{errors.shortDescription.message}</p>
        ) : null}
      </div>

      <div className="sm:col-span-2">
        <label className="text-body-sm text-ink" htmlFor="pf-long">
          Long description <span className="text-muted">(optional)</span>
        </label>
        <Textarea id="pf-long" disabled={disabled} rows={4} {...reg("longDescription")} />
      </div>

      <div className="sm:col-span-2">
        <label className="text-body-sm text-ink" htmlFor="pf-classical">
          Classical reference <span className="text-muted">(optional)</span>
        </label>
        <Input id="pf-classical" disabled={disabled} {...reg("classicalReference")} />
      </div>

      <label className="text-body-sm text-ink flex items-center gap-2 sm:col-span-2">
        <input type="checkbox" disabled={disabled} {...reg("subscribable")} />
        Subscribable (Subscribe &amp; Save)
      </label>
    </>
  );
}
